import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CForm,
  CFormLabel,
} from "@coreui/react";
import AppToast from "../../components/seller/AppToast";
import AppLoader from "../../components/seller/AppLoader";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReverseBids,
  storeReverseBid,
} from "../../features/seller/sellerSlice";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";

const RequestedBids = () => {
  const dispatch = useDispatch();

  const {
    list: reverseBids,
    loading,
    error,
  } = useSelector((state) => state.seller.reverseBids);

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [totalAmount, setTotalAmount] = useState("");

  useEffect(() => {
    dispatch(fetchReverseBids())
      .unwrap()
      .catch((err) => {
        console.error("Failed to load reverse bids:", err);
      });
  }, [dispatch]);

  const filtered = reverseBids.filter((r) =>
    (r.scrap_type || "").toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  const handleAcceptClick = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleConfirmAccept = async () => {
    if (!totalAmount || isNaN(totalAmount) || Number(totalAmount) <= 0) {
      setToastType("danger");
      setToastMessage("Please enter a valid total amount.");
      setToastVisible(true);
      return;
    }

    const id = selectedRequest.id;
    const data = { total_amount: Number(totalAmount) };

    try {
      const response = await dispatch(storeReverseBid({ id, data })).unwrap();

      setShowModal(false);
      setToastType("success");
      setToastMessage(response?.message || "Bid accepted successfully.");
      setToastVisible(true);
      setTotalAmount("");
      dispatch(fetchReverseBids());
    } catch (err) {
      setToastType("danger");
      setToastMessage(err || "Failed to accept the bid.");
      setToastVisible(true);
    }
  };

  if (loading) {
    return <AppLoader />;
  }

  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">Requested Bids</h4>
          </CCol>
        </CRow>

        <CCard>
          <CCardHeader>
            <CRow className="mb-2">
              <CCol>
                <h6 className="text-muted mb-0">All Requested Scrap</h6>
              </CCol>
            </CRow>
            <hr className="my-2" />
            <CRow className="align-items-center">
              <CCol md={6}>
                Show{" "}
                <CFormSelect
                  size="sm"
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  style={{ display: "inline-block", width: "auto" }}
                  options={[
                    { label: "5", value: "5" },
                    { label: "10", value: "10" },
                    { label: "25", value: "25" },
                  ]}
                />{" "}
                entries
              </CCol>
              <CCol md={6} className="text-end">
                Search:{" "}
                <CFormInput
                  type="text"
                  size="sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ display: "inline-block", width: "200px" }}
                />
              </CCol>
            </CRow>
          </CCardHeader>

          <CCardBody>
            {/* <div className="custom-scrollbar"> */}
            <CTable responsive bordered hover>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Requirement ID</CTableHeaderCell>
                  <CTableHeaderCell>Category</CTableHeaderCell>
                  <CTableHeaderCell>Sub Category</CTableHeaderCell>
                  <CTableHeaderCell>Scrap Type</CTableHeaderCell>
                  <CTableHeaderCell>Quantity</CTableHeaderCell>
                  <CTableHeaderCell>Unit</CTableHeaderCell>
                  <CTableHeaderCell>Location</CTableHeaderCell>
                  <CTableHeaderCell>Description</CTableHeaderCell>
                  <CTableHeaderCell>Preferred Date</CTableHeaderCell>
                  <CTableHeaderCell>Status</CTableHeaderCell>
                  <CTableHeaderCell>Created At</CTableHeaderCell>
                  <CTableHeaderCell>Action</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {currentData.map((item, index) => (
                  <CTableRow key={item.id}>
                    <CTableDataCell>{indexOfFirst + index + 1}</CTableDataCell>
                    <CTableDataCell>{item.unique_requirement_id || "-"}</CTableDataCell>
                    <CTableDataCell>{item.category_name || "-"}</CTableDataCell>
                    <CTableDataCell>
                      {item.subcategory_name || "-"}
                    </CTableDataCell>
                    <CTableDataCell>{item.scrap_type || "-"}</CTableDataCell>
                    <CTableDataCell>{item.quantity || "-"}</CTableDataCell>
                    <CTableDataCell>{item.unit || "-"}</CTableDataCell>
                    <CTableDataCell>{item.location || "-"}</CTableDataCell>
                    <CTableDataCell>
                      <div
                        className="ql-editor"
                        style={{ padding: 0 }}
                        dangerouslySetInnerHTML={{
                          __html: item.description || "-",
                        }}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.preferred_delivery_date || "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span
                        className={`badge bg-${item.status === "fulfilled"
                            ? "success"
                            : item.status === "pending"
                              ? "warning"
                              : "info"
                          }`}
                      >
                        {capitalizeFirst(item.status) || "-"}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      {timeAgo(item.created_at) || "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.status === "open" ? (
                        <CButton
                          size="sm"
                          color="success"
                          onClick={() => handleAcceptClick(item)}
                        >
                          Accept
                        </CButton>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {currentData.length === 0 && (
                  <CTableRow>
                    <CTableDataCell
                      colSpan="12"
                      className="text-center text-muted"
                    >
                      No Requested bid found.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
            {/* </div> */}

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
                entries
              </div>
              <div className="d-flex align-items-center">
                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Prev
                </CButton>

                {[...Array(totalPages)].map((_, i) => (
                  <CButton
                    key={i}
                    size="sm"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                    className="me-2"
                  >
                    {i + 1}
                  </CButton>
                ))}

                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <CModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          alignment="center"
        >
          <CCard className="m-0">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Accept Requested Bid</h5>
                <CButton
                  color="light"
                  className="p-0"
                  onClick={() => setShowModal(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </CButton>
              </div>

              <CForm>
                <CFormLabel htmlFor="totalAmount" className="fw-semibold">
                  Enter Total Amount (AUD)
                </CFormLabel>
                <CFormInput
                  id="totalAmount"
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="E.g. 15000"
                  min={0}
                />

                <div className="d-flex justify-content-end mt-4">
                  <CButton
                    color="primary"
                    className="me-2"
                    onClick={handleConfirmAccept}
                  >
                    Confirm Accept
                  </CButton>
                  <CButton
                    color="secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </CButton>
                </div>
              </CForm>
            </CCardBody>
          </CCard>
        </CModal>
      </div>

      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default RequestedBids;
